/**
 * Générateur — Automatisme : compléter « 20 % de 120 = … » et
 * « 30 = …/100 × 1 000 ».
 * Attendu ID : 1129 | Niveau : 4ème | Domaine : Proportionnalité
 * Rubrique : AUTOMATISME (programme 2026, Proportionnalité 4e) — rituel court.
 * Labels (cartes exerciseur) :
 *   - Compléter a % de c = …          (saisie ; QCM à leurres calculés)
 *   - Par étapes                      (texte à trous : 10 % de c, donc a % de c)
 *   - Compléter …/100 × c            (saisie : le pourcentage, ou c ; QCM)
 *   - Vrai ou faux ?                  (V/F sur des égalités des deux sortes)
 *   - Pourcentages : vrai ou faux ?   (V/F de pur rappel)
 *
 * ── Plan de cartes validé (2026-09-08) ─────────────────────────────────────
 * Source : BO cycle 4 (2026), Proportionnalité 4e, automatisme « Compléter :
 * 20 % de 120 = … ; 30 = …/100 × 1 000 » + grille P1-P12. P1 : CM2 introduit
 * (fraction d'une quantité), 6e formalisé (pourcentage = fraction sur 100,
 * fraction opérateur sur un nombre), 5e automatisme (1 %, 10 %, 50 %),
 * 4e étendu à un a quelconque et à l'égalité à trou dans les deux sens.
 * P2 : sens installés — a % = a sur 100, a % de c = a/100 × c, 10 % puis
 * multiplier (linéarité) ; non installés (distracteurs) — coefficient
 * multiplicateur, quatrième proportionnelle, produit en croix, augmentation.
 * P3 : a dans {5, 15, 20, 30, 40, 60, 80}, c multiple de 20 ≤ 400 (1 000 pour
 * 5, 20, 30), résultats entiers ; pour
 * …/100 × c, c ∈ {50, 100, 200, 500, 1 000} et pourcentage entier.
 * P4 : égalité à trou (automatisme 4e « multiplications à trou »). P6 :
 * aucune calculatrice. P7 : pas de produit en croix ni de coefficient. P8 :
 * pourcentage, « sur 100 », « de ». P9 : 10 % et 1 % d'un nombre, ×/÷ 10,
 * 100, 1 000 restent implicites. P10 : constat. P11 : 120 − 20 ; a × c sans
 * ÷ 100 ; recopier 30 (ou 0,03) dans le trou de 30 = …/100 × 1 000. P12 :
 * rémanence forte.
 * INVARIANTS :
 *   - tous les calculs en ENTIERS : a × c divisible par 100 ; p × 100
 *     divisible par c ;
 *   - leurres calculés, distincts, positifs, bornés à c ; V/F : pas de
 *     crochets, mixité 1-3 faux, familles exclusives, fenêtre stricte.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeSaisie, makeTexteTrou,
    retirerCodesErreur,
} from '../../../utils';

const ATTENDU_ID = 1129;
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_DE = 'Compléter a % de c = …';
const L_ETAPES = 'Par étapes';
const L_ECRITURE = 'Compléter …/100 × c';
const L_VF = 'Vrai ou faux ?';
const L_RAPPEL = 'Pourcentages : vrai ou faux ?';

/** Entier avec espace fine des milliers (1 000). */
const mille = (n) => (Math.abs(n) >= 1000 ? String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ' ') : String(n));

// ─── a % de c : a multiple de 5 (hors 10, 25, 50, 100 du 1128), c multiple de 20 ──
// Calcul mental (décision prof 2026-09-08) : a dans {5, 15, 20, 30, 40, 60, 80}, c ≤ 400 (1 000 pour 5, 20, 30 seulement).
const AS = [5, 15, 20, 30, 40, 60, 80];
const CS = [40, 60, 80, 120, 140, 160, 180, 200, 240, 300, 320, 360, 400, 500, 600, 800, 1000];
const deC = (a, c) => (a * c) / 100;                                  // entier par construction (c multiple de 20)

// ─── Codes d'erreur (contenu.erreurs, lus par trouverErreurs) ──────────────
// Gestes faux nommés de « a % de c » (leurres du QCM) : − a, ÷ 10 au lieu de ÷ 100, la part qui reste, ÷ a.
// Lot 2 (04/10/2026, docs/SFD_CODES_ERREUR.md) : la part qui reste garde complement dans « a % de c » ; dans
// « … = …/100 × c », c'est complement_du_pourcentage.
const erreursDe = (a, c) => [['moins_a', c - a], ['pourcent_lu_dixiemes', deC(a, c) * 10], ['complement', c - deC(a, c)], ['divise_par_a', c / a]];
/** Les erreurs sûres : valeurs positives, ni la bonne réponse, ni portées par deux codes. */
const erreursSures = (paires, juste) => {
    const parValeur = [];
    paires.forEach(([code, v]) => {
        if (!Number.isFinite(v) || v <= 0 || Math.abs(v - juste) < 1e-9) return;
        const deja = parValeur.find((p) => Math.abs(p.v - v) < 1e-9);
        if (deja) deja.codes.add(code); else parValeur.push({ v, codes: new Set([code]) });
    });
    return parValeur.filter((p) => p.codes.size === 1).map((p) => ({ code: [...p.codes][0], v: p.v }));
};
const auCentiemePres = (x) => Math.abs(x * 100 - Math.round(x * 100)) < 1e-9;

const tirerDe = (as = AS) => {
    for (let essai = 0; essai < 100; essai++) {
        const a = randomChoice(as); const c = randomChoice(CS);
        if (!Number.isInteger(deC(a, c))) continue;
        if (c > 400 && ![5, 20, 30].includes(a)) continue;                 // calcul mental : 60 % de 800 non
        return { a, c, v: deC(a, c) };
    }
    return { a: 20, c: 120, v: 24 };
};

// ─── …/100 × c : c diviseur de 100 000 simple, pourcentage entier ──────────
// Revue 2026-09-08 (prof) : 20 et 10 retirés (nombre de départ < 100, lecture peu naturelle) ; 500 gardé (table de 5).
const CS_ECR = [50, 100, 200, 500, 1000];
const AS_ECR = [1, 2, 3, 4, 5, 6, 8, 10, 12, 15, 20, 25, 30, 40, 50, 60, 75, 80];
const tirerEcriture = () => {
    for (let essai = 0; essai < 100; essai++) {
        // Revue 2026-09-08 (A8) : c = 100 (le trou se recopie) reste un rappel à 10 %.
        const a = randomChoice(AS_ECR); const c = Math.random() < 0.1 ? 100 : randomChoice(CS_ECR.filter((x) => x !== 100)); const p = (a * c) / 100;
        if (!Number.isInteger(p) || p === 0) continue;
        return { a, c, p };
    }
    return { a: 3, c: 1000, p: 30 };
};

// ─── Anti-répétition ───────────────────────────────────────────────────────
const antiRepeat = (construire, ref) => {
    let payload = construire();
    for (let t = 0; t < 8 && payload._meta.params.signature === ref.v; t++) payload = construire();
    ref.v = payload._meta.params.signature;
    return payload;
};
const refS = { v: null }; const refQ = { v: null }; const refT = { v: null }; const refE = { v: null }; const refEQ = { v: null };

// ─── Carte 1a : compléter a % de c = … (saisie) ───────────────────────────

const construireDeSaisie = () => {
    const { a, c, v } = tirerDe();
    const payload = makeSaisie(String(v), `<p>Complète : ${a} % de ${mille(c)} = …</p>`, opts(L_DE));
    payload.contenu = { ...payload.contenu, sousType: `a${a}` };
    payload._meta.params = { a, c, v, signature: `${a}-${c}` };
    // Valeurs exactes au centième seulement, écrites avec la virgule décimale.
    const erreurs = erreursSures(erreursDe(a, c), v).filter((e) => auCentiemePres(e.v)).map((e) => ({ code: e.code, valeur: String(Math.round(e.v * 100) / 100).replace('.', ',') }));
    if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
    return payload;
};
const genDeSaisie = () => antiRepeat(construireDeSaisie, refS);

// ─── Carte 1b : compléter a % de c = … (QCM à leurres calculés) ───────────

const construireDeQcm = () => {
    for (let essai = 0; essai < 60; essai++) {
        const { a, c, v } = tirerDe();
        const pool = [
            { v: c - a, role: 'moins_a' },                    // 120 − 20
            { v: c / 10, role: 'dix_pour_cent' },             // 10 % seulement
            { v: a, role: 'a_lui_meme' },                     // le pourcentage recopié
            { v: v * 10, role: 'dix_fois' },                  // ÷ 10 au lieu de ÷ 100
            { v: v / 10, role: 'dixieme' },
            { v: c / a, role: 'divise_par_a' },               // 120 ÷ 20
            { v: c - v, role: 'complement' },                 // le reste (80 %)
        ];
        // leurres entiers, positifs, ≤ c, distincts de la bonne et entre eux
        const cands = [];
        shuffle(pool).forEach((l) => { if (Number.isInteger(l.v) && l.v > 0 && l.v <= c && l.v !== v && !cands.some((m) => m.v === l.v)) cands.push(l); });
        if (cands.length < 3) continue;
        const leurres = cands.slice(0, 3);
        const options = shuffle([v, ...leurres.map((l) => l.v)].map(mille));
        const payload = makeQcm(options, mille(v), `<p>Complète : ${a} % de ${mille(c)} = …</p>`, opts(L_DE));
        payload.contenu = { ...payload.contenu, sousType: `a${a}` };
        payload._meta.params = { a, c, v, roles: leurres.map((l) => l.role), signature: `${a}-${c}` };
        const erreurs = erreursSures(erreursDe(a, c), v).filter((e) => Number.isInteger(e.v)).map((e) => ({ code: e.code, valeur: mille(e.v) })).filter((e) => options.includes(e.valeur));
        if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
        return payload;
    }
    throw new Error('gen_1129 : QCM sans leurres');
};
const genDeQcm = () => antiRepeat(construireDeQcm, refQ);

// ─── Carte 2 : par étapes (texte à trous : 10 % de c, donc a % de c) ──────

const AS_ETAPES = [5, 20, 30, 40, 60, 80];                          // un seul geste après 10 % : × k ou ÷ 2 (15 % en demande deux)
const construireEtapes = () => {
    const { a, c, v } = tirerDe(AS_ETAPES);
    const dix = c / 10;
    // Revue 2026-09-08 (prof) : la chaîne du calcul est écrite en entier, sur deux lignes, en mode SAISIE
    // (le 10 % retapé en ligne 2 est un geste cohérent, pas une recopie d'étiquette).
    const ligne2 = a === 5
        ? `5 % de ${mille(c)}, c'est la moitié de 10 %, donc 5 % de ${mille(c)} = {{${dix}}} ÷ 2 = {{${v}}}`
        : `${a} % de ${mille(c)}, c'est ${a / 10} fois 10 %, donc ${a} % de ${mille(c)} = ${a / 10} × {{${dix}}} = {{${v}}}`;
    const phrase = `10 % de ${mille(c)} = {{${dix}}}
${ligne2}`;
    const payload = makeTexteTrou(phrase, `<p>Complète les deux étapes du calcul.</p>`, { ...opts(L_ETAPES), mode: 'saisie' });
    payload.contenu = { ...payload.contenu, sousType: `a${a}` };
    payload._meta.params = { a, c, v, dix, signature: `${a}-${c}` };
    return payload;
};
const genEtapes = () => antiRepeat(construireEtapes, refT);

// ─── Carte 3a : compléter …/100 × c (saisie : le pourcentage, ou c) ───────

const construireEcritureSaisie = () => {
    const cas = randomChoice(['a', 'a', 'c']);                       // le trou sur c, une fois sur trois
    const { a, c, p } = tirerEcriture();
    const texte = cas === 'a'
        ? `<p>Complète : ${mille(p)} = …/100 × ${mille(c)}</p>`
        : `<p>Complète : ${mille(p)} = ${a}/100 × …</p>`;
    const payload = makeSaisie(String(cas === 'a' ? a : c), texte, opts(L_ECRITURE));
    payload.contenu = { ...payload.contenu, sousType: cas };
    payload._meta.params = { cas, a, c, p, signature: `${cas}-${a}-${c}` };
    // Trou du pourcentage : la part qui reste (100 − a), comme au QCM ; le trou sur c reste sans code.
    if (cas === 'a') {
        const erreurs = erreursSures([['complement_du_pourcentage', 100 - a]], a).map((e) => ({ code: e.code, valeur: String(e.v) }));
        if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
    }
    return payload;
};
const genEcritureSaisie = () => antiRepeat(construireEcritureSaisie, refE);

// ─── Carte 3b : compléter …/100 × c (QCM sur le pourcentage) ──────────────

const construireEcritureQcm = () => {
    for (let essai = 0; essai < 60; essai++) {
        const { a, c, p } = tirerEcriture();
        // Revue 2026-09-08 (A2/A3) : leurres bornés à 100 (au-delà, l'égalité est impossible), et tirage équilibré
        // du nombre de leurres SOUS la bonne réponse (0 à 3) — elle était la plus petite option dans 49 % des tirages.
        const pool = [
            { v: p, role: 'recopie_p' },                      // 30 recopié dans le trou
            { v: a * 10, role: 'dix_fois' },
            { v: 100 - a, role: 'complement' },
            { v: c / 100, role: 'c_sur_cent' },
            { v: p / 10, role: 'p_sur_dix' },
            { v: a / 10, role: 'a_sur_dix' },
            { v: a + 1, role: 'voisin_plus' },
            { v: a - 1, role: 'voisin_moins' },
        ];
        const valide = (l, pris) => Number.isInteger(l.v) && l.v > 0 && l.v <= 100 && l.v !== a && !pris.some((m) => m.v === l.v);
        const dessous = shuffle(pool.filter((l) => l.v < a)); const dessus = shuffle(pool.filter((l) => l.v > a));
        const nBas = randInt(0, 3);
        const leurres = [];
        dessous.forEach((l) => { if (leurres.length < nBas && valide(l, leurres)) leurres.push(l); });
        dessus.forEach((l) => { if (leurres.length < 3 && valide(l, leurres)) leurres.push(l); });
        if (leurres.length < 3) continue;
        const options = shuffle([a, ...leurres.map((l) => l.v)].map(mille));
        const payload = makeQcm(options, mille(a), `<p>Complète : ${mille(p)} = …/100 × ${mille(c)}</p><p>Quel nombre va dans le trou ?</p>`, opts(L_ECRITURE));
        payload.contenu = { ...payload.contenu, sousType: 'a' };
        payload._meta.params = { a, c, p, roles: leurres.map((l) => l.role), signature: `q-${a}-${c}` };
        const erreurs = erreursSures([['complement_du_pourcentage', 100 - a]], a).map((e) => ({ code: e.code, valeur: mille(e.v) })).filter((e) => options.includes(e.valeur));
        if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
        return payload;
    }
    throw new Error('gen_1129 : QCM écriture sans leurres');
};
const genEcritureQcm = () => antiRepeat(construireEcritureQcm, refEQ);

// ─── Carte 4 : vrai ou faux ? (égalités des deux sortes) ───────────────────

let dernieresLignesVf1129 = [];

const genVf = () => {
    for (let essai = 0; essai < 160; essai++) {
        // deux égalités « a % de c = v », deux écritures « p = a/100 × c », chacune vraie ou fausse
        const candidates = [];
        for (let k = 0; k < 2; k++) {
            const { a, c, v } = tirerDe();
            const fauxV = shuffle([c - a, v * 10, c / 10, c - v, c / a]).find((x) => Number.isInteger(x) && x > 0 && x !== v && x <= c);
            const vrai = Math.random() < 0.5 || fauxV === undefined;
            const code = vrai ? null : (erreursSures(erreursDe(a, c), v).find((e) => Math.abs(e.v - fauxV) < 1e-9) || {}).code;
            candidates.push({ l: `${a} % de ${mille(c)} = ${mille(vrai ? v : fauxV)}.`, v: vrai, f: `de${k}`, code });
        }
        for (let k = 0; k < 2; k++) {
            const { a, c, p } = tirerEcriture();
            // Revue 2026-09-08 (A3) : pourcentage faux borné à 100 (p < c dans toutes les écritures du gen).
            const fauxA = shuffle([p, a * 10, 100 - a, c / 100, a + 1, a - 1]).find((x) => Number.isInteger(x) && x > 0 && x <= 100 && x !== a);
            const vrai = Math.random() < 0.5 || fauxA === undefined;
            const code = vrai || fauxA !== 100 - a ? null : 'complement_du_pourcentage';                    // seule la part qui reste est nommée
            candidates.push({ l: `${mille(p)} = ${mille(vrai ? a : fauxA)}/100 × ${mille(c)}.`, v: vrai, f: `ecr${k}`, code });
        }
        const nbFaux = candidates.filter((it) => !it.v).length;
        if (nbFaux < 1 || nbFaux > 3) continue;
        if (new Set(candidates.map((it) => it.l)).size !== 4) continue;
        const lignes = shuffle(candidates);
        if (essai > 120) dernieresLignesVf1129 = [];
        if (lignes.some((it) => dernieresLignesVf1129.includes(it.l))) continue;
        dernieresLignesVf1129 = lignes.map((it) => it.l);
        const payload = makeVraiFaux(
            lignes.map((it) => it.l),
            lignes.map((it) => (it.v ? 0 : 1)),
            '<p>Pour chaque égalité, dis si elle est vraie ou fausse.</p>',
            opts(L_VF),
        );
        payload.contenu = { ...payload.contenu, cadence: 'raisonnement' };
        payload._meta.params = { lignes: lignes.map((it) => it.l) };
        const erreurs = lignes.filter((it) => !it.v && it.code).map((it) => ({ code: it.code, ligne: it.l }));
        if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
        return payload;
    }
    throw new Error('gen_1129 : V/F sans tirage après 160 essais');
};

// ─── Carte 5 : pourcentages, vrai ou faux ? (pur rappel) ───────────────────

const POOL_RAPPEL = [
    // Revue 2026-09-08 (prof) : pas de lettres dans les affirmations de rappel, tout est dit avec des mots.
    { l: 'Pour calculer un pourcentage d\'un nombre, on divise le pourcentage par 100, puis on multiplie par le nombre.', v: true, f: 'formule' },
    { l: 'Pour calculer un pourcentage d\'un nombre, on multiplie le pourcentage par le nombre.', v: false, f: 'formule', code: 'multiplie_par_a' },
    { l: 'Pour calculer un pourcentage d\'un nombre, on divise le nombre par le pourcentage.', v: false, f: 'formule', code: 'divise_par_a' },
    { l: '20 % de 120, c\'est 120 − 20.', v: false, f: 'soustraction', code: 'moins_a' },
    { l: '20 % de 120, c\'est 20 centièmes de 120.', v: true, f: 'soustraction' },
    { l: 'Pour prendre 20 % d\'un nombre, on prend 10 % de ce nombre, puis le double.', v: true, f: 'chemin' },
    { l: 'Pour prendre 5 % d\'un nombre, on prend 10 % de ce nombre, puis la moitié.', v: true, f: 'chemin' },
    { l: 'Pour prendre 30 % d\'un nombre, on divise ce nombre par 30.', v: false, f: 'chemin', code: 'divise_par_a' },
    { l: '3/100 × 1 000, c\'est 3 % de 1 000, soit 30.', v: true, f: 'ecriture' },
    { l: '30/100 × 1 000, c\'est 30.', v: false, f: 'ecriture' },
    { l: 'Dans l\'égalité 30 = …/100 × 1 000, le nombre qui manque est 3.', v: true, f: 'trou' },
    { l: 'Dans l\'égalité 30 = …/100 × 1 000, le nombre qui manque est 30.', v: false, f: 'trou' },
    { l: 'Quand le pourcentage est plus petit que 100 %, le résultat est plus petit que le nombre de départ.', v: true, f: 'borne' },
    { l: 'Le résultat d\'un pourcentage est toujours plus grand que le nombre de départ.', v: false, f: 'borne' },
];

let dernieresLignesRappel1129 = [];

const genRappelVf = () => {
    for (let essai = 0; essai < 160; essai++) {
        const fausses = shuffle(POOL_RAPPEL.filter((it) => !it.v)); const vraies = shuffle(POOL_RAPPEL.filter((it) => it.v));
        const nbFaux = randInt(1, 3);
        const lignes = shuffle([...fausses.slice(0, nbFaux), ...vraies.slice(0, 4 - nbFaux)]);
        if (new Set(lignes.map((it) => it.f)).size !== 4) continue;
        if (essai > 120) dernieresLignesRappel1129 = [];
        if (lignes.some((it) => dernieresLignesRappel1129.includes(it.l))) continue;
        dernieresLignesRappel1129 = lignes.map((it) => it.l);
        const payload = makeVraiFaux(
            lignes.map((it) => it.l),
            lignes.map((it) => (it.v ? 0 : 1)),
            '<p>Pour chaque affirmation sur les pourcentages, dis si elle est vraie ou fausse.</p>',
            opts(L_RAPPEL),
        );
        payload._meta.params = { lignes: lignes.map((it) => POOL_RAPPEL.indexOf(it)) };
        const erreurs = lignes.filter((it) => !it.v && it.code).map((it) => ({ code: it.code, ligne: it.l }));
        if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
        return payload;
    }
    throw new Error('gen_1129 : V/F rappel sans tirage après 160 essais');
};

// ─── Registre ──────────────────────────────────────────────────────────────

const GENERATORS = {
    de_saisie: genDeSaisie,
    de_qcm_unique: genDeQcm,
    etapes_texte_trou: genEtapes,
    ecriture_saisie: genEcritureSaisie,
    ecriture_qcm_unique: genEcritureQcm,
    vf_tableau_vf: genVf,
    rappel_tableau_vf: genRappelVf,
};

const FORMAT_LABELS = { saisie: 'Saisie', qcm_unique: 'QCM', texte_trou: 'Texte à trous', tableau_vf: 'Vrai/Faux' };

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'de', label: L_DE, formatsList: buildFormats('de') },
    { id: 'etapes', label: L_ETAPES, formatsList: buildFormats('etapes') },
    { id: 'ecriture', label: L_ECRITURE, formatsList: buildFormats('ecriture') },
    { id: 'vf', label: L_VF, formatsList: buildFormats('vf') },
    { id: 'rappel', label: L_RAPPEL, formatsList: buildFormats('rappel') },
];

// Codes retirés le 03/10/2026 après la lecture à l'aveugle (docs/kit/REGLES_EQUIPE.md) : le nom ne
// décrit pas le choix construit, ou deux erreurs donnent le même nombre. Le choix reste proposé,
// il part sans nom.
const CODES_RETIRES = {
    rappel_tableau_vf: ['multiplie_par_a'],
};

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    retirerCodesErreur(payload, CODES_RETIRES[key]);
    payload._meta.generatorKey = key;
    return payload;
};
